import {useCallback, useId, type ReactElement} from 'react'
import {CheckIcon, StopIcon} from '@primer/octicons-react'
import {Button, Link} from '@primer/react'
import CircleOcticon from '@github-ui/circle-octicon'

import styles from './RebasingStacksSection.module.css'
import {HEADER_ICON_SIZE} from '../../../constants'
import {EffectiveEntryStatus} from '../../../helpers/effective-entry-status'
import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-status'
import {RebaseState, useStackRebaseStatus} from '../../../hooks/mutations/use-stack-rebase-mutation'
import {CenteredLoadingSpinner} from '../common/CenteredLoadingSpinner'
import {MergeBoxExpandable} from '../common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from '../common/MergeBoxSectionHeader'
import {ResolveConflictsInstructions} from '../common/ResolveConflictsDialog'
import {RebasingStacksList} from './RebasingStacksList'
import {useRebaseAnnouncements} from './use-rebase-announcements'

/**
 * When this is rendered, no other section should be rendered in MergeBox.
 * The rebase is started by the confirmation dialog (via MergeBox) before this
 * section mounts; this section only observes progress.
 * The section will show the status of each entry being rebased.
 * The terminal state (success, failure, or partial success), surfaces a "Done"
 * CTA. There is intentionally no way to leave the section before then;
 */
export function RebasingStacksSection({
  baseRefName,
  stackEntries,
  stackNumber,
  rebaseFailed,
  onDone,
}: {
  baseRefName?: string
  stackEntries: StackSectionStackEntry[]
  stackNumber?: number
  rebaseFailed: boolean
  onDone: () => void
}) {
  const headerId = useId()
  const {isRebasing, status: rebaseState, clearStatus} = useStackRebaseStatus()

  // Move focus into the this section as soon as it mounts. Entering this section
  // unmounts the rebase confirmation dialog's trigger, so the focus it tries to
  // restore lands on nothing and falls back to the document body. Focusing the
  // labelled region keeps focus in a sensible place and lets a screen reader
  // announce the new focus position (WCAG 2.4.3 Focus Order). `tabIndex={-1}` allows this
  // programmatic focus without adding the region to the tab sequence.
  const focusRegionOnMount = useCallback((node: HTMLElement | null) => {
    node?.focus()
  }, [])

  // Filter out downstack entries that are already merged or queued as they are not rebased
  const rebasingEntries = stackEntries.filter(entry => {
    if (entry.position !== 'BEFORE') return true
    const isMerged = entry.pull.state === 'MERGED' || entry.effectiveEntryStatus === EffectiveEntryStatus.Merged
    const isQueued = entry.effectiveEntryStatus === EffectiveEntryStatus.Queued
    return !isMerged && !isQueued
  })

  const entries = Object.values(rebaseState)
  const total = entries.length
  const succeededCount = entries.filter(e => e.state === RebaseState.Rebased).length
  const failedCount = entries.filter(
    e => e.state === RebaseState.FailedToRebase || e.state === RebaseState.MergeConflicts,
  ).length
  const hasPending = entries.some(e => e.state === RebaseState.Rebasing)

  // The single entry that stopped the rebase, if any. A merge conflict takes
  // precedence over a generic failure for messaging. Its PR URL (used for the
  // link in the subtitle) comes from the matching stack entry.
  const conflictedEntry = entries.find(e => e.state === RebaseState.MergeConflicts)
  const failedEntry = entries.find(e => e.state === RebaseState.FailedToRebase)
  const stoppedEntry = conflictedEntry ?? failedEntry
  const stoppedPullUrl = stoppedEntry
    ? stackEntries.find(entry => entry.pull.number === stoppedEntry.number)?.pull.url
    : undefined
  const conflictInstructionsStackNumber = conflictedEntry !== undefined ? stackNumber : undefined
  const showResolveConflictInstructions = conflictInstructionsStackNumber !== undefined

  // Terminal once the mutation is no longer running, we have per-entry results,
  // and nothing is still pending, or once the rebase request itself failed.
  const isTerminal = rebaseFailed || (!isRebasing && total > 0 && !hasPending)

  // Only the success (green check) icon should show when the rebase fully
  // succeeded; a request error (rebaseFailed) is a failure even though no entry is FAILED.
  const showSuccessIcon = isTerminal && failedCount === 0 && !rebaseFailed

  // `subtitle` is what we render (may include a link to the offending PR);
  // `subtitleText` is the plain-text equivalent announced to assistive tech.
  let title: string
  let subtitleText: string
  let subtitle: ReactElement | string
  if (conflictedEntry) {
    title = 'Rebasing was stopped due to merge conflicts'
    subtitleText = `Resolve the merge conflicts in pull request #${conflictedEntry.number}, then try to rebase this stack again.`
    subtitle = (
      <>
        Resolve the merge conflicts in pull request{' '}
        <Link inline href={stoppedPullUrl}>
          #{conflictedEntry.number}
        </Link>{' '}
        , then try to rebase this stack again.
      </>
    )
  } else if (failedEntry) {
    title = 'Rebasing was stopped due to an error'
    subtitleText = `Check pull request #${failedEntry.number} for any issues, then try to rebase this stack again.`
    subtitle = (
      <>
        Check pull request{' '}
        <Link inline href={stoppedPullUrl}>
          #{failedEntry.number}
        </Link>{' '}
        for any issues, then try to rebase this stack again.
      </>
    )
  } else if (rebaseFailed) {
    title = 'Rebasing was stopped due to a network error'
    subtitleText = 'Try to rebase this stack again. If the problem persists, refresh the page.'
    subtitle = subtitleText
  } else if (!isTerminal) {
    title = 'Rebasing stack…'
    subtitleText = `${succeededCount} of ${rebasingEntries.length} branches rebased`
    subtitle = subtitleText
  } else {
    title = 'Rebasing complete'
    subtitleText = 'This stack is up to date and has a linear commit history.'
    subtitle = subtitleText
  }

  useRebaseAnnouncements({
    title,
    subtitle: subtitleText,
    rebaseFailed,
    isTerminal,
    failedCount,
    showResolveConflictInstructions,
  })

  return (
    <section
      ref={focusRegionOnMount}
      tabIndex={-1}
      aria-label="Rebasing stacked pull requests"
      aria-describedby={headerId}
      className={`${styles.RebasingStacksSection} border-bottom color-border-subtle`}
    >
      <MergeBoxSectionHeader
        headerId={headerId}
        title={title}
        subtitle={subtitle}
        icon={
          isTerminal ? (
            <CircleOcticon
              className={
                showSuccessIcon
                  ? 'bgColor-success-emphasis fgColor-onEmphasis'
                  : 'bgColor-danger-emphasis fgColor-onEmphasis'
              }
              size={HEADER_ICON_SIZE}
              icon={() => (showSuccessIcon ? <CheckIcon size={16} /> : <StopIcon size={16} />)}
            />
          ) : (
            <CenteredLoadingSpinner className={styles.RebasingStacksSectionSpinner} />
          )
        }
        rightSideContent={
          isTerminal ? (
            <Button
              onClick={() => {
                clearStatus()
                onDone()
              }}
            >
              Done
            </Button>
          ) : undefined
        }
      />
      <MergeBoxExpandable isExpanded contentPaddingInline="none">
        <RebasingStacksList
          baseRefName={baseRefName}
          stackEntries={rebasingEntries}
          rebaseStatus={rebaseState}
          rebaseFailed={rebaseFailed}
        />
      </MergeBoxExpandable>
      {conflictInstructionsStackNumber !== undefined && (
        <div className={styles.RebasingStacksSectionFooter}>
          <ResolveConflictsInstructions stackNumber={conflictInstructionsStackNumber} variant="inline" />
        </div>
      )}
    </section>
  )
}
